import React, { Fragment, useEffect, useRef, useState } from "react";
import { OrderHistoryTable } from "./orderHistoryTable";
import { clearAll as clearOrderHistoryCache, readFilterState, saveFilterState } from "./orderHistoryCache";
import moment from "moment";
import { BacktoMyAccount } from "../backToMyAccount";
import { CustomDateRangePicker } from "../../../Layout/Common/customDateRangePicker";
import { useAuth } from "../../../Context/AuthContext";
import { useLocation, useParams } from "react-router-dom";
import { normalizePath } from "../../../Layout/Common/common";
import { OrderFilterMobile } from "./orderFilterMobile";

const defaultRange = () => ({
  startDate: moment().startOf("year").format("DD/MM/YYYY"),
  endDate: moment().format("DD/MM/YYYY"),
});

export const OrderHistory = () => {
  const { userId } = useAuth();
  const [restoredFilters] = useState(() => readFilterState(userId));
  const [range, setRange] = useState(() =>
    restoredFilters ? { startDate: restoredFilters.startDate, endDate: restoredFilters.endDate } : {},
  );
  const [searchStr, setSearchStr] = useState(restoredFilters ? restoredFilters.searchString : "");
  const [filters, setFilters] = useState(null);
  const pickerRef = useRef();
  const searchRef = useRef();
  const mobileFilterRef = useRef();
  const location = useLocation();
  const { page } = useParams();
  const isInvoiceSearch = String(searchStr || "").trim().length > 0;

  useEffect(() => {
    if (restoredFilters) {
      handleSearchClick(
        { startDate: restoredFilters.startDate, endDate: restoredFilters.endDate },
        restoredFilters.searchString,
      );
      return;
    }
    let startDate = moment().startOf("year").format("DD/MM/YYYY");
    let endDate = moment().format("DD/MM/YYYY");
    handleSearchClick({ startDate: startDate, endDate: endDate });
  }, []);

  const handleSearchClick = (dtRange, searchOverride) => {
    clearOrderHistoryCache();
    let startDate =
      dtRange && dtRange.startDate
        ? dtRange.startDate
        : range && range.startDate
          ? range.startDate
          : moment().startOf("year").format("DD/MM/YYYY");

    let endDate =
      dtRange && dtRange.endDate
        ? dtRange.endDate
        : range && range.endDate
          ? range.endDate
          : moment().format("DD/MM/YYYY");

    const searchString =
      searchOverride !== undefined ? searchOverride : searchRef.current ? searchRef.current.value : "";

    const isSearchByNumber = String(searchString).trim().length > 0;

    setFilters({
      fromDate: isSearchByNumber ? "" : String(startDate),
      toDate: isSearchByNumber ? "" : String(endDate),
      searchString: searchString,
    });
    setRange({ startDate: startDate, endDate: endDate });
    saveFilterState(userId, { startDate: startDate, endDate: endDate, searchString: searchString });
  };

  function handleShowFilterClick() {
    mobileFilterRef.current?.show();
  }

  const handleMobileApply = (dtRange, searchString) => {
    setSearchStr(searchString);
    if (searchRef.current) {
      searchRef.current.value = searchString;
    }
    handleSearchClick(dtRange, searchString);
  };
  const handleReset = () => {
    setSearchStr("");
    if (searchRef.current) {
      searchRef.current.value = "";
    }
    setRange({});
    handleSearchClick(
      { startDate: moment().startOf("year").format("DD/MM/YYYY"), endDate: moment().format("DD/MM/YYYY") },
      "",
    );
    pickerRef.current.reset();
  };

  return (
    <Fragment>
      <div className="container-fluid container-lg">
        <div className="row">
          <div className="col-lg-12">
            <div className="acc-header py-3">
              <div className="myaccount-address myAccount-header">
                <h1 className="myaccount-title">My Order History</h1>
                <p className="ordersFilter-show" onClick={handleShowFilterClick}>
                  <span className="wes-filter" />
                  Filter Orders
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="row pb-3">
          <div className="col-lg-12">
            <div className="invoice-acc-search py-3">
              <div className="input-group orders-search">
                <i className="fa-solid fa-magnifying-glass" />
                <input
                  type="text"
                  className="form-control"
                  placeholder="Order No or Invoice No"
                  aria-label="Username"
                  value={searchStr}
                  ref={searchRef}
                  onChange={(e) => {
                    setSearchStr(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key == "Enter") {
                      e.preventDefault();
                      handleSearchClick();
                    }
                  }}
                  aria-describedby="basic-addon1"
                />
              </div>

              <div className="search-myOrders mt-3 mt-sm-0">
                <div className={`orders-view-daterange${isInvoiceSearch ? " is-disabled" : ""}`}>
                  <CustomDateRangePicker
                    onChange={(dt) => handleSearchClick(dt || defaultRange())}
                    ref={pickerRef}
                    showRanges
                    getDefaultStart={() => moment().startOf("year")}
                    opens="right"
                    disabled={isInvoiceSearch}
                    initialRange={
                      restoredFilters
                        ? { startDate: restoredFilters.startDate, endDate: restoredFilters.endDate }
                        : null
                    }
                  />
                  <span className="wes-calendar" />
                  {isInvoiceSearch && (
                    <p className="orders-daterange-note">
                      <i className="fa-solid fa-circle-info" />
                      Date filter is not applied when searching by Order/Invoice No.
                    </p>
                  )}
                </div>
                <div className="popupBtns">
                  <div className="button-effect popupBtns-two">
                    <button
                      type="submit"
                      className="btn btn-products theme-primary"
                      onClick={() => handleSearchClick(false)}
                    >
                      Search
                    </button>
                    <button type="button" className="btn btn-products btn-unavailable" onClick={handleReset}>
                      Reset
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-lg-12">
            <div className="myAccountTable">{filters && <OrderHistoryTable filters={filters} />}</div>
          </div>
          <BacktoMyAccount />
        </div>
      </div>
      <OrderFilterMobile
        ref={mobileFilterRef}
        searchValue={searchStr}
        range={range}
        onApply={handleMobileApply}
        onReset={handleReset}
      />
    </Fragment>
  );
};
